import React, { useState } from "react";
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Button, Menu, Plus, DotsVertical, Edit01, Gift01, Ticket01, Download01, Trash01 } from "@opendate-app/setlist";
import { useSeriesShowContext } from "../SeriesShowApp";
import SortableTableRow from "../../shared/components/SortableTableRow";
import SortableTableHandleHeader from "../../shared/components/SortableTableHandleHeader";
import CreatePassProductModal from "./CreatePassProductModal";
import EditPassProductModal from "./EditPassProductModal";
import RedemptionSetupModal from "./RedemptionSetupModal";
import ComplimentaryPassModal from "./ComplimentaryPassModal";
import ConfirmDialog from "./ConfirmDialog";
import InventoryPoolsCard from "./InventoryPoolsCard";
import PassHolderLimitCard from "./PassHolderLimitCard";
import SeriesPassSurchargesCard from "./SeriesPassSurchargesCard";
import { toastError, toastSuccess } from "../../../shared/toastHelper";
import { fmtPrice } from "../passFormatting";
import { formatVenueDate, formatVenueDateTime } from "../../../shared/venueZoneFormatting";

// The venue's fee rules are edited independently of the pass (ActiveAdmin), so a
// price that saved cleanly can stop being chargeable later with nothing
// revalidating it. When that happens the public page silently drops the product,
// so this is the organizer's only warning before the pass stops selling.
const UNCHARGEABLE_REASONS = {
  outside_fee_rules: "falls outside this venue's fee rules",
  below_minimum: 'totals less than the minimum a pass can be charged',
  above_maximum: 'totals more than the maximum a pass can be charged',
};

const KIND_LABELS = {
  season_pass: "Season Pass",
  flex_pack: "Flex Pack",
};

const PassesTab = () => {
  const {
    series,
    canManageSeries,
    reorderInFlight,
    handleDeletePassProduct,
    handleExportPassHolders,
    handleReorderPassProduct,
  } = useSeriesShowContext();
  const products = series.series_pass_products || [];
  const reorderable = canManageSeries ?? false;
  const pools = series.series_pass_inventory_pools || [];
  const poolName = (product) => {
    const pool = pools.find((candidate) => candidate.id === product.series_pass_inventory_pool_id);
    return pool ? pool.name : null;
  };
  const currency = series.base_currency_symbol;
  const [showCreate, setShowCreate] = useState(false);
  const [redemptionProduct, setRedemptionProduct] = useState(null);
  const [editProduct, setEditProduct] = useState(null);
  const [deleteProduct, setDeleteProduct] = useState(null);
  const [complimentaryTarget, setComplimentaryTarget] = useState(null);
  const complimentaryProduct = complimentaryTarget
    ? products.find((product) => product.id === complimentaryTarget.id) ?? complimentaryTarget
    : null;
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const productName = (id) => products.find((product) => product.id === id)?.name ?? "a pass that is no longer listed";
  const reorderAnnouncements = {
    onDragStart: ({ active }) => `Picked up ${productName(active.id)}.`,
    onDragOver: ({ active, over }) => (over ? `${productName(active.id)} is over ${productName(over.id)}.` : undefined),
    onDragEnd: ({ active, over }) => {
      if (!over) return `${productName(active.id)} was dropped.`;
      if (active.id === over.id) return `${productName(active.id)} was dropped back in place.`;
      return `${productName(active.id)} was moved to where ${productName(over.id)} was.`;
    },
    onDragCancel: ({ active }) => `Moving ${productName(active.id)} was cancelled.`,
  };

  const reorderProducts = async ({ active, over }) => {
    if (!over) return;
    if (active.id === over.id) return;

    try {
      await handleReorderPassProduct(active.id, over.id);
    } catch (e) {
      toastError(e?.response?.data?.errors?.[0] || "Could not save the new order. Please try again.");
    }
  };

  const confirmDelete = async (product) => {
    try {
      await handleDeletePassProduct(product.id);
    } catch (e) {
      toastError("Could not delete this product. Please try again.");
    }
  };

  const exportPassHolders = async (product) => {
    try {
      await handleExportPassHolders(product.id);
      toastSuccess("Export started. We'll email you a download link when the file is ready.");
    } catch (e) {
      const apiErrors = e && e.response && e.response.data;
      toastError(
        Array.isArray(apiErrors) && apiErrors.length > 0
          ? apiErrors.join(", ")
          : "Could not export the answers. Please try again.",
      );
    }
  };

  const productActions = (product) => [
    { label: "Edit product", icon: Edit01, onClick: () => setEditProduct(product) },
    {
      label: product.redemption_stamped_at ? "Re-run redemption" : "Set up redemption",
      icon: Ticket01,
      onClick: () => setRedemptionProduct(product),
    },
    ...(series.state !== "canceled" && canManageSeries
      ? [{ label: "Comp passes", icon: Gift01, onClick: () => setComplimentaryTarget(product) }]
      : []),
    ...((product.custom_fields || []).length > 0
      ? [{ label: "Export answers", icon: Download01, onClick: () => exportPassHolders(product) }]
      : []),
    { label: "Delete product", icon: Trash01, destructive: true, onClick: () => setDeleteProduct(product) },
  ];

  return (
    <>
    <div className="series-schedule-card">
      <div className="series-schedule-header d-flex align-items-center justify-content-between">
        <div>
          <h4 className="series-schedule-title mb-0">Passes &amp; Packs</h4>
          <div className="text-muted small" data-testid="passes-subtitle">
            Sell one purchase that covers several dates in this collection. Dates added later are covered automatically.
          </div>
        </div>
        {series.can_sell_series_passes && (
          <Button
            variant="primary"
            icon={Plus}
            data-testid="create-pass-product-btn"
            onClick={() => setShowCreate(true)}
          >
            Create pass or pack
          </Button>
        )}
      </div>

      {products.length === 0 ? (
        <div className="series-detail-card-body text-muted" data-testid="passes-empty">
          No passes or packs yet. Create a Season Pass or Flex Pack, then auto-create its per-show redemption tickets.
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={reorderProducts}
          accessibility={{ announcements: reorderAnnouncements }}
        >
          <table className="table table-hover mb-0 series-schedule-table" data-testid="passes-table">
            <thead>
              <tr>
                {reorderable && <SortableTableHandleHeader className="series-schedule-th" />}
                <th className="series-schedule-th series-schedule-th-first">Name</th>
                <th className="series-schedule-th">Kind</th>
                <th className="series-schedule-th">Price</th>
                <th className="series-schedule-th">Capacity</th>
                <th className="series-schedule-th">Sold</th>
                <th className="series-schedule-th">Remaining</th>
                <th className="series-schedule-th">Delivery</th>
                <th className="series-schedule-th">Status</th>
                <th className="series-schedule-th"></th>
              </tr>
            </thead>
            <tbody>
              <SortableContext items={products.map((product) => product.id)} strategy={verticalListSortingStrategy}>
                {products.map((product) => (
                  <SortableTableRow
                    key={product.id}
                    id={product.id}
                    handleLabel={`Reorder ${product.name}`}
                    sortable={reorderable}
                    disabled={reorderInFlight ?? false}
                    className="series-schedule-row"
                  >
                    <td className="series-schedule-td-date series-schedule-td-strong">
                      {product.name}
                      {poolName(product) && (
                        <div className="text-muted small font-weight-normal" data-testid={`pool-label-${product.id}`}>
                          {`Shared: ${poolName(product)}`}
                        </div>
                      )}
                    </td>
                    <td className="series-schedule-td">{KIND_LABELS[product.product_kind] || product.product_kind}</td>
                    <td className="series-schedule-td">
                      {fmtPrice(product.price, currency)}
                      {product.upcoming_price_point && (
                        <div
                          className="text-muted series-schedule-upcoming-price"
                          data-testid={`upcoming-price-${product.id}`}
                        >
                          {`${fmtPrice(product.upcoming_price_point.price, currency)} on ${formatVenueDateTime(product.upcoming_price_point.starts_at)}`}
                        </div>
                      )}
                      {(product.unchargeable_prices || []).length > 0 && (
                        <div
                          className="series-schedule-unchargeable"
                          data-testid={`unchargeable-${product.id}`}
                        >
                          {(product.unchargeable_prices || [])
                            .map((entry) => `${fmtPrice(entry.price, currency)} ${UNCHARGEABLE_REASONS[entry.reason] || 'cannot be charged'}`)
                            .join('. ')}
                          {'. Fans cannot buy this pass at that price.'}
                        </div>
                      )}
                    </td>
                    <td className="series-schedule-td">{product.capacity}</td>
                    <td className="series-schedule-td series-schedule-td-strong" data-testid={`sold-cell-${product.id}`}>
                      {product.sold_count}
                    </td>
                    <td className="series-schedule-td" data-testid={`remaining-cell-${product.id}`}>
                      {product.sold_out ? (
                        <span className="series-status-pill series-status-past">Sold out</span>
                      ) : (
                        product.sellable_remaining
                      )}
                    </td>
                    <td className="series-schedule-td" data-testid={`delivery-cell-${product.id}`}>
                      {product.delivery_status === "Scheduled" && product.delivery_time
                        ? `Scheduled · ${formatVenueDate(product.delivery_time)}`
                        : product.delivery_status}
                    </td>
                    <td className="series-schedule-td">
                      <span className={`series-status-pill ${product.active ? "series-status-confirm" : "series-status-past"}`}>
                        {product.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="series-schedule-td series-schedule-td-actions">
                      <div className="d-flex align-items-center justify-content-end series-schedule-actions">
                        <Menu
                          placement="bottom-end"
                          items={productActions(product)}
                          trigger={(
                            <Button
                              variant="tertiary"
                              size="sm"
                              icon={DotsVertical}
                              aria-label={`Actions for ${product.name}`}
                              data-testid={`product-actions-btn-${product.id}`}
                            />
                          )}
                        />
                      </div>
                    </td>
                  </SortableTableRow>
                ))}
              </SortableContext>
            </tbody>
          </table>
        </DndContext>
      )}

      <CreatePassProductModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
      />
      <EditPassProductModal
        product={editProduct}
        onClose={() => setEditProduct(null)}
      />
      <RedemptionSetupModal
        product={redemptionProduct}
        onClose={() => setRedemptionProduct(null)}
      />
      <ComplimentaryPassModal
        product={complimentaryProduct}
        onClose={() => setComplimentaryTarget(null)}
      />
      <ConfirmDialog
        open={deleteProduct !== null}
        title="Delete this product?"
        body={
          deleteProduct
            ? `Delete "${deleteProduct.name}"? This can't be undone. Redemption tickets already stamped on shows are not removed.`
            : ""
        }
        confirmLabel="Delete product"
        onConfirm={() => confirmDelete(deleteProduct)}
        onClose={() => setDeleteProduct(null)}
      />
    </div>
    <PassHolderLimitCard />
    <InventoryPoolsCard />
    {series.charges_processing_by_ticket && <SeriesPassSurchargesCard />}
    </>
  );
};

export default PassesTab;
